/**
 * Projects API Adapter
 *
 * This adapter implements the IProjectsGateway interface,
 * bridging the application layer with the actual API implementation.
 * It handles data mapping between API responses and application DTOs.
 */

import type {
  CreateProjectDTO,
  ProjectDTO,
  ProjectRecordDTO,
  UpdateProjectDTO,
} from "@/application/projects/dtos/ProjectDTO";
import type { IProjectsGateway } from "@/application/projects/ports/IProjectsGateway";
import {
  createProject as apiCreateProject,
  deleteProject as apiDeleteProject,
  getProjectDetails as apiGetProjectDetails,
  getProjectRecords as apiGetProjectRecords,
  getProjects as apiGetProjects,
  getAdminProjects as apiGetAdminProjects,
  updateProject as apiUpdateProject,
  type ProjectRecordResponse,
  type ProjectResponse,
} from "@/lib/projectsApi";

export class ProjectsApiAdapter implements IProjectsGateway {
  async getProjects(): Promise<ProjectDTO[]> {
    const response = await apiGetProjects();
    return response.map((p) => this.mapProjectResponseToDTO(p));
  }

  async getProjectDetails(projectId: string): Promise<ProjectDTO> {
    const response = await apiGetProjectDetails(projectId);
    return this.mapProjectResponseToDTO(response);
  }

  async getProjectRecords(projectId: string): Promise<ProjectRecordDTO[]> {
    const response = await apiGetProjectRecords(projectId);
    return response.map((r) => this.mapProjectRecordResponseToDTO(r));
  }

  async createProject(data: CreateProjectDTO): Promise<ProjectDTO> {
    const response = await apiCreateProject(
      data.name,
      data.description,
      data.researchGoals,
      data.interviewGuide
    );
    return this.mapProjectResponseToDTO(response);
  }

  async updateProject(
    projectId: string,
    data: UpdateProjectDTO
  ): Promise<ProjectDTO> {
    const updates: {
      name?: string;
      description?: string;
      status?: string;
      research_goals?: string;
      interview_guide?: string;
    } = {};

    // Map DTO fields to API fields
    if (data.name !== undefined) updates.name = data.name;
    if (data.description !== undefined) updates.description = data.description;
    if (data.status !== undefined) {
      // Convert lowercase status to API format (capitalize first letter)
      updates.status =
        data.status.charAt(0).toUpperCase() + data.status.slice(1);
    }
    if (data.researchGoals !== undefined)
      updates.research_goals = data.researchGoals;
    if (data.interviewGuide !== undefined)
      updates.interview_guide = data.interviewGuide;

    const response = await apiUpdateProject(projectId, updates);
    return this.mapProjectResponseToDTO(response);
  }

  async deleteProject(projectId: string): Promise<void> {
    await apiDeleteProject(projectId);
  }

  async getAllProjectsAdmin(): Promise<ProjectDTO[]> {
    const response = await apiGetAdminProjects();
    return response.map((p) => this.mapProjectResponseToDTO(p));
  }

  /**
   * Maps API ProjectResponse to application ProjectDTO
   */
  private mapProjectResponseToDTO = (response: ProjectResponse): ProjectDTO => {
    return {
      id: response.id,
      name: response.name,
      description: response.description,
      status: this.normalizeStatus(response.status),
      recordsCount: response.records_count,
      projectReportsCount: response.total_reports_count ?? 0,
      latestReportsCount: response.latest_reports_count ?? 0,
      activeInterviewRoomsCount: response.active_interview_rooms_count ?? 0,
      createdAt: response.created_at,
      researchGoals: response.research_goals ?? null,
      interviewGuide: response.interview_guide ?? null,
      ownerEmail: response.owner_email ?? null,
      fileSearchIndex: response.file_search_index
        ? {
            status: response.file_search_index.status,
            indexedDocumentsCount:
              response.file_search_index.indexed_documents_count ?? null,
            pendingOperationsCount:
              response.file_search_index.pending_operations_count ?? null,
            indexedDocumentsProgressPercent:
              response.file_search_index.indexed_documents_progress_percent ?? null,
            lastIndexedAt: response.file_search_index.last_indexed_at,
            updatedAt: response.file_search_index.updated_at,
            error: response.file_search_index.error,
          }
        : null,
    };
  };

  /**
   * Maps API ProjectRecordResponse to application ProjectRecordDTO
   */
  private mapProjectRecordResponseToDTO = (
    response: ProjectRecordResponse
  ): ProjectRecordDTO => {
    return {
      id: response.id,
      name: response.name,
      fileName: response.file_name,
      fileExtension: response.file_extension,
      status: response.status,
      isPublic: response.is_public,
      isArchived: response.is_archived,
      sizeMb: response.size_mb,
      durationSec: response.duration_sec,
      creationDate: response.creation_date,
      modifiedDate: response.modified_date,
      hasTranscript: response.has_transcript,
      hasReports: response.has_reports,
    };
  };

  /**
   * Normalizes status from API format to application format
   * API returns: "New", "Opened", "Closed"
   * Application expects: "new", "opened", "closed"
   */
  private normalizeStatus = (status: string): "new" | "opened" | "closed" => {
    const normalized = status.toLowerCase();
    if (
      normalized === "new" ||
      normalized === "opened" ||
      normalized === "closed"
    ) {
      return normalized as "new" | "opened" | "closed";
    }
    // Default to "new" if status is unknown
    return "new";
  };
}
